import React from 'react';
import {
  Box, Button, Chip, Divider, IconButton, Paper, Tooltip, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { RADIUS, brandColors, brandCss } from '../../theme/appTheme';
import { statusChip } from './SuperAdminDataTable';

export interface SubscriptionPlanRecord {
  _id: string;
  planName: string;
  description?: string;
  hsnSac?: string;
  monthlyPrice: number;
  yearlyPrice: number;
  userLimit?: number;
  vehicleLimit?: number;
  driverLimit?: number;
  branchLimit?: number;
  gpsDeviceLimit?: number;
  storageLimit?: number;
  status?: string;
}

interface SubscriptionPlanCardProps {
  plan: SubscriptionPlanRecord;
  formatMoney: (amount: number) => string;
  onEdit?: (plan: SubscriptionPlanRecord) => void;
  onToggleStatus?: (plan: SubscriptionPlanRecord) => void;
  selected?: boolean;
  onSelect?: (plan: SubscriptionPlanRecord) => void;
  compact?: boolean;
}

const LIMIT_ROWS = [
  { key: 'userLimit', label: 'Users' },
  { key: 'vehicleLimit', label: 'Vehicles' },
  { key: 'driverLimit', label: 'Drivers' },
  { key: 'branchLimit', label: 'Branches' },
  { key: 'gpsDeviceLimit', label: 'GPS' },
  { key: 'storageLimit', label: 'Storage (GB)' },
] as const;

export const SubscriptionPlanCard: React.FC<SubscriptionPlanCardProps> = ({
  plan,
  formatMoney,
  onEdit,
  onToggleStatus,
  selected,
  onSelect,
  compact,
}) => {
  const active = plan.status !== 'inactive';
  const clickable = Boolean(onSelect);

  return (
    <Paper
      elevation={0}
      onClick={clickable ? () => onSelect?.(plan) : undefined}
      sx={{
        p: compact ? 2 : 2.5,
        height: '100%',
        borderRadius: `${RADIUS}px`,
        border: `2px solid ${selected ? brandCss.primary : active ? brandColors.border : '#fecaca'}`,
        bgcolor: selected ? brandCss.green50 : '#fff',
        position: 'relative',
        overflow: 'hidden',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'all 0.22s ease',
        '&:hover': {
          transform: clickable || onEdit ? 'translateY(-3px)' : undefined,
          boxShadow: brandCss.shadowLg,
          borderColor: selected ? brandCss.primary : '#bbf7d0',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: active ? brandCss.gradientPrimary : 'linear-gradient(90deg,#fca5a5,#f87171)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Box sx={{ minWidth: 0, pr: 1 }}>
          <Typography fontWeight={800} fontSize={compact ? 16 : 18} color={brandColors.textDark} noWrap>
            {plan.planName}
          </Typography>
          {plan.description && (
            <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.25 }} noWrap>
              {plan.description}
            </Typography>
          )}
        </Box>
        {statusChip(plan.status || 'active')}
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography fontWeight={800} fontSize={compact ? 22 : 26} color={brandCss.primaryDark} lineHeight={1.1}>
          {formatMoney(plan.monthlyPrice)}
          <Typography component="span" fontSize={13} fontWeight={600} color="text.secondary"> /mo</Typography>
        </Typography>
        <Typography fontSize={13} color="text.secondary" fontWeight={600}>
          {formatMoney(plan.yearlyPrice)} /yr
        </Typography>
      </Box>

      <Chip
        label={`HSN ${plan.hsnSac || '998314'}`}
        size="small"
        sx={{
          mb: 1.5,
          fontWeight: 700,
          fontSize: 11,
          bgcolor: brandCss.green100,
          color: brandCss.primaryDark,
        }}
      />

      <Divider sx={{ mb: 1.5 }} />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: onEdit || onToggleStatus ? 2 : 0 }}>
        {LIMIT_ROWS.map(({ key, label }) => (
          <Typography key={key} fontSize={12} color="text.secondary">
            <Box component="span" sx={{ fontWeight: 700, color: brandColors.textDark }}>
              {plan[key] ?? '—'}
            </Box>
            {' '}{label}
          </Typography>
        ))}
      </Box>

      {(onEdit || onToggleStatus) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {onEdit && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon fontSize="small" />}
              onClick={(e) => { e.stopPropagation(); onEdit(plan); }}
              sx={{ flex: 1, textTransform: 'none', fontWeight: 600, borderRadius: `${RADIUS}px` }}
            >
              Edit
            </Button>
          )}
          {onToggleStatus && (
            <Tooltip title={active ? 'Deactivate plan' : 'Activate plan'}>
              <IconButton
                size="small"
                color={active ? 'error' : 'success'}
                onClick={(e) => { e.stopPropagation(); onToggleStatus(plan); }}
                sx={{ border: `1px solid ${brandColors.border}`, borderRadius: `${RADIUS}px` }}
              >
                {active ? <BlockIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Paper>
  );
};

interface SubscriptionPlanGridProps {
  plans: SubscriptionPlanRecord[];
  formatMoney: (amount: number) => string;
  onEdit?: (plan: SubscriptionPlanRecord) => void;
  onToggleStatus?: (plan: SubscriptionPlanRecord) => void;
  selectedPlanName?: string;
  selectedPlanId?: string;
  onSelect?: (plan: SubscriptionPlanRecord) => void;
  compact?: boolean;
}

export const SubscriptionPlanGrid: React.FC<SubscriptionPlanGridProps> = ({
  plans,
  formatMoney,
  onEdit,
  onToggleStatus,
  selectedPlanName,
  selectedPlanId,
  onSelect,
  compact,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        lg: `repeat(${Math.min(plans.length || 1, 3)}, 1fr)`,
      },
      gap: 2,
    }}
  >
    {plans.map((plan) => (
      <SubscriptionPlanCard
        key={plan._id}
        plan={plan}
        formatMoney={formatMoney}
        onEdit={onEdit}
        onToggleStatus={onToggleStatus}
        selected={selectedPlanId ? selectedPlanId === plan._id : selectedPlanName === plan.planName}
        onSelect={onSelect}
        compact={compact}
      />
    ))}
  </Box>
);
